import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { sendToken, verifyToken } from "@/services/api/Master";
import { claimVCPage } from "@/services/api/VentureCapital";
import {
  checkEmailDomainWithList,
  getBaseDomainWithList,
  isValidEmail,
} from "@/utils/checkEmailDomain";
import { CheckCircle } from "@phosphor-icons/react";
import { Question } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Spinner from "../Spinner";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

export const ClaimInvestor = ({
  user,
  investor,
  getVCClaimUserData,
  setOpenClaimVCPopup,
  openClaimVCPopup,
}) => {
  const [isPartner, setIsPartner] = useState(false);
  const [state, setState] = useState({
    name: user?.name || "",
    is_founding_partner: "",
    vc_website: investor.website,
    phone_number: user?.phone_number || "",
    user_corporate_email: "",
    role: user?.role || "",
    founding_partner_email: "",
    user_linkedin_url: "",
  });

  const [confirmedOfficialEmail, setConfirmedOfficialEmail] = useState(false);
  const [isSendingVerifyEmail, setIsSendingVerifyEmail] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] =
    useState(false);
  const [confirmToken, setConfirmToken] = useState("");
  const [isSameUserEmailAndWebsite, setIsSameUserEmailAndWebsite] =
    useState(false);
  const [emailConfirmLoading, setEmailConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [placeholderEmails, setPlaceholderEmails] = useState("");

  function setHandlePartner() {
    setIsPartner((state) => !state);
  }

  const handleChange = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    const fetchPlaceholderEmails = async () => {
      if (investor.website) {
        const result = await getBaseDomainWithList(investor.website);
        if (result.verificationEmails?.length > 0) {
          setPlaceholderEmails(result.verificationEmails.join(", "));
        } else {
          setPlaceholderEmails(`xxxxxx@${result.baseDomain}`);
        }
      }
    };

    fetchPlaceholderEmails();
  }, [investor.website]);

  //   useEffect(() => {
  //     const checkInitialEmail = async () => {
  //       if (user.email && investor.website) {
  //         const isValid = await checkEmailDomainWithList(
  //           user.email,
  //           investor.website
  //         );
  //         setConfirmedOfficialEmail(isValid);
  //         setIsSameUserEmailAndWebsite(isValid);
  //         if (isValid) {
  //           setState((prevState) => ({
  //             ...prevState,
  //             user_corporate_email: user.email,
  //           }));
  //         }
  //       }
  //     };
  //     checkInitialEmail();
  //   }, [user.email, investor.website]);

  useEffect(() => {
    if (!isOpen) {
      setState({
        name: user?.name || "",
        is_founding_partner: "",
        vc_website: investor.website,
        phone_number: user?.phone_number || "",
        user_corporate_email: "",
        role: user?.role || "",
        founding_partner_email: "",
        user_linkedin_url: "",
      });
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!state.name) {
      return toast.error("Your name is required!");
    }

    if (
      !isSameUserEmailAndWebsite &&
      state.user_corporate_email &&
      !confirmedOfficialEmail
    ) {
      return toast.error("Official email is not verified yet!");
    }

    if (!isPartner && !state.founding_partner_email) {
      return toast.error("Funding partner email is required!");
    }

    if (!isPartner && !state.role) {
      return toast.error("Role is required!");
    }

    if (!state.user_linkedin_url) {
      return toast.error("Your Linkedin url is required!");
    }

    setLoading(true);
    try {
      const response = await claimVCPage({
        ...state,
        vc_id: investor.id,
        user_id: user.id,
        is_founding_partner: isPartner,
      });
      setLoading(false);
      let message = "";
      if (isPartner) {
        message =
          "Your request has been submitted. We will send you an email update regarding the status of your request.";
      } else {
        message =
          "Your request has been submitted. We have sent an email to your funding partner for approval. We will notify you via email once the approval process is completed.";
      }
      setOpenClaimVCPopup(null);
      toast.success(message, { duration: 6000 });
      getVCClaimUserData();
    } catch (error) {
      setLoading(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      }
    }
  };

  const onVerifyToken = async () => {
    if (!confirmToken) toast.error("Token is required!");
    setEmailConfirmLoading(true);
    try {
      const response = await verifyToken({ token: confirmToken });
      setConfirmedOfficialEmail(true);
      toast.success(`Successfully verify the email!`);
    } catch (error) {
      toast.error("Code is invalid or expired.");
    }
    setEmailVerificationLoading(false);
  };

  const onSendToken = async () => {
    if (!state.user_corporate_email) toast.error("Official Email is required!");

    setEmailVerificationLoading(true);
    try {
      const response = await sendToken({ email: state.user_corporate_email });
      toast.success(
        `Verification token just sent to ${state.user_corporate_email}, use that token to verify the email.`,
        {
          duration: 4000,
        }
      );
      setIsSendingVerifyEmail(true);
    } catch (error) {}
    setEmailVerificationLoading(false);
  };

  const onCheckEmailWithDomain = async () => {
    if (state.user_corporate_email && investor.website) {
      const isValid = await checkEmailDomainWithList(
        state.user_corporate_email,
        investor.website
      );
      setIsSameUserEmailAndWebsite(isValid);
    }
  };

  const onClearCorporateEmail = () => {
    setState({
      ...state,
      user_corporate_email: "",
    });
    setConfirmedOfficialEmail(false);
    setIsSendingVerifyEmail(false);
    setConfirmToken("");
  };

  return (
    <Dialog open={openClaimVCPopup} onOpenChange={setOpenClaimVCPopup}>
      <DialogContent className='flex flex-col p-4 !gap-0 bg-white lg:p-5 text-background'>
        <div className='pb-2'>
          <p className='mb-1 font-semibold'>Claim {investor.name}?</p>
          <p className='text-sm text-background/60'>
            Hey! So you&apos;d like to claim this investor page. We&apos;re
            excited to hand over the editing access to you, but before we do -
            we just need a few details from you to go through our security
            protocols.
          </p>
        </div>

        <div className='flex-1 overflow-y-auto max-h-[60vh] py-4 text-left grid grid-cols-12 gap-x-3 gap-y-4'>
          <div className='w-full col-span-12 space-y-1.5'>
            <Label className='text-xs text-background'>Name</Label>
            <Input
              name='name'
              onChange={handleChange}
              value={state.name}
              className='bg-white text-background border-background/20 focus-visible:ring-0'
            />
          </div>
          <div className='col-span-12'>
            <Label className='w-full text-xs text-background'>
              Are you the founding partner?
            </Label>
            <RadioGroup
              defaultValue={isPartner}
              onChange={setHandlePartner}
              className='flex mt-3 space-x-4'
              disabled={loading}
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem
                  value={true}
                  id='yes'
                  onClick={setHandlePartner}
                  className='border-background/30 data-[state=checked]:bg-background'
                />
                <Label htmlFor='yes' className='text-xs text-background'>
                  Yes
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem
                  value={false}
                  id='no'
                  onClick={setHandlePartner}
                  className='border-background/30 data-[state=checked]:bg-background'
                />
                <Label htmlFor='no' className='text-xs text-background'>
                  No
                </Label>
              </div>
            </RadioGroup>
          </div>
          {!isPartner && (
            <>
              <div className='w-full col-span-12 lg:col-span-6 space-y-1.5'>
                <Label className='text-xs text-background'>Role</Label>
                <Input
                  name='role'
                  value={state.role}
                  onChange={handleChange}
                  className='bg-white text-background border-background/20 focus-visible:ring-0'
                  disabled={loading}
                />
              </div>
              <div className='w-full col-span-12 lg:col-span-6 space-y-1.5'>
                <Label className='text-xs text-background'>
                  Founding Partner Email
                </Label>
                <Input
                  name='founding_partner_email'
                  type='email'
                  className='bg-white text-background border-background/20 focus-visible:ring-0'
                  onChange={handleChange}
                  value={state.founding_partner_email}
                />
                <small className='text-xs opacity-50'>
                  An email will be sent for approval
                </small>
              </div>
            </>
          )}
          <div className='w-full col-span-12 lg:col-span-6 space-y-1.5'>
            <Label className='text-xs text-background'>LinkedIn URL</Label>
            <Input
              name='user_linkedin_url'
              value={state.user_linkedin_url}
              onChange={handleChange}
              className='bg-white text-background border-background/20 focus-visible:ring-0'
              disabled={loading}
            />
          </div>
          <div className='w-full col-span-12 lg:col-span-6 space-y-1.5'>
            <Label className='text-xs text-background'>Investor Website</Label>
            <Input
              name='vc_website'
              value={state.vc_website}
              onChange={handleChange}
              className='bg-white text-background border-background/20 focus-visible:ring-0'
              disabled
            />
          </div>
          <div className='w-full col-span-12 space-y-2 lg:col-span-6'>
            <Label className='flex items-center justify-between mt-1.5 text-xs text-background'>
              <div className='flex items-center gap-1'>
                <p>Your Corporate Email</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Question
                        weight='bold'
                        className='w-3.5 h-3.5 text-blue-400'
                      />
                    </TooltipTrigger>
                    <TooltipContent className='max-w-md'>
                      <p className='text-xs'>
                        Your corporate email must match the VC website URL. If
                        it doesn’t, click the box below, and we’ll go through a
                        manual approval process to get you access.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {(confirmedOfficialEmail || isSendingVerifyEmail) && (
                <small
                  onClick={onClearCorporateEmail}
                  className='float-right cursor-pointer'
                >
                  Remove
                </small>
              )}
            </Label>
            <Input
              name='user_corporate_email'
              type='email'
              className='bg-white text-background border-background/20 focus-visible:ring-0'
              onChange={handleChange}
              value={state.user_corporate_email}
              placeholder={placeholderEmails}
              onBlur={onCheckEmailWithDomain}
              disabled={confirmedOfficialEmail || loading}
            />
            <div>
              {!confirmedOfficialEmail &&
              state.user_corporate_email &&
              !isSendingVerifyEmail ? (
                <div className='flex items-center justify-between mt-2'>
                  <div>
                    {!isSameUserEmailAndWebsite && (
                      <small className='text-[11px]'>
                        Differ from vc website
                      </small>
                    )}
                  </div>
                  <Button
                    variant='secondary'
                    className='h-6 text-xs'
                    onClick={onSendToken}
                    disabled={
                      !isValidEmail(state.user_corporate_email) ||
                      emailVerificationLoading ||
                      (isValidEmail(state.user_corporate_email) &&
                        !isSameUserEmailAndWebsite) ||
                      loading
                    }
                  >
                    {emailVerificationLoading
                      ? "Processing..."
                      : "Verify Email"}
                  </Button>
                </div>
              ) : (
                confirmedOfficialEmail && (
                  <div className='inline-flex items-center float-right text-xs text-green-600 rounded-md'>
                    <p className='mr-1'>Email is verified</p>
                    <CheckCircle weight='fill' />
                  </div>
                )
              )}
            </div>
            {isSameUserEmailAndWebsite &&
              isSendingVerifyEmail &&
              !confirmedOfficialEmail && (
                <>
                  <div className='w-full col-span-8 space-y-1.5'>
                    <Label className='text-xs' htmlFor='confirm_token'>
                      Verification Token
                      <span className='text-red-700'>*</span>
                    </Label>
                    <Input
                      type='text'
                      id='confirm_token'
                      className='h-12 border border-0 bg-white/50 backdrop-blur-md placeholder:text-background/50 text-slate-700'
                      value={confirmToken}
                      name='confirm_token'
                      onChange={(e) => setConfirmToken(e.target.value)}
                      placeholder='xxxxxx'
                    />
                  </div>
                  {
                    <Button
                      size={"xl"}
                      className='w-full h-8 col-span-4 mt-8 space-y-1.5 font-medium text-white bg-slate-600 bg-background hover:bg-slate-700'
                      onClick={onVerifyToken}
                      disabled={
                        !state.user_corporate_email ||
                        emailVerificationLoading ||
                        loading
                      }
                    >
                      {emailVerificationLoading ? "Processing..." : "Confirm"}
                    </Button>
                  }
                </>
              )}
          </div>
          <div className='w-full col-span-12 lg:col-span-6 space-y-1.5'>
            <Label className='text-xs text-background'>
              Phone Number <span className='text-slate-400'>(Optional)</span>
            </Label>
            <Input
              name='phone_number'
              value={state.phone_number}
              onChange={handleChange}
              className='bg-white text-background border-background/20 focus-visible:ring-0'
            />
          </div>
          <div className='col-span-12 space-y-2'>
            <label className='flex space-x-2.5 cursor-pointer'>
              <Checkbox className={`border-background/30 `} />
              <span className='text-xs leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-background/70'>
                My email address does not match our VC Firm Website URL.
              </span>
            </label>
            <label className='flex space-x-2 cursor-pointer'>
              <Checkbox className={`border-background/30 `} />
              <span className='text-xs leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-background/70'>
                I would like to book a 20min Free DEMO on the Lucidity Insights
                Platform
              </span>
            </label>
          </div>
        </div>

        <div className='col-span-12 pt-4 border-t border-gray-200'>
          <Button
            variant='secondary'
            size={"lg"}
            className='w-full font-medium'
            onClick={handleSubmit}
            disabled={
              !isSameUserEmailAndWebsite ||
              !confirmedOfficialEmail ||
              !isValidEmail(state.user_corporate_email) ||
              loading
            }
          >
            {!loading ? "Claim Investor" : <Spinner />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
